"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { authFetch } from "@/lib/authFetch";

export interface EoaBalances {
  pol: number;
  usdce: number;
  usdc: number;
  pusd: number;
  totalUsd: number;
}

export interface EoaBalanceState {
  eoa: string | null;
  depositWallet: string | null;
  balances: EoaBalances | null;
  needsMove: boolean;
  needsGas: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

const POLL_INTERVAL_MS = 60_000;

export function useEoaBalance(): EoaBalanceState {
  const { authenticated, ready } = usePrivy();
  const [eoa, setEoa] = useState<string | null>(null);
  const [depositWallet, setDepositWallet] = useState<string | null>(null);
  const [balances, setBalances] = useState<EoaBalances | null>(null);
  const [needsMove, setNeedsMove] = useState(false);
  const [needsGas, setNeedsGas] = useState(false);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBalance = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch("/api/wallet/eoa-balance");
      if (!res.ok) return;
      const data = await res.json();
      setEoa(data.eoa ?? null);
      setDepositWallet(data.deposit_wallet ?? null);
      setBalances(data.balances ?? null);
      setNeedsMove(!!data.needsMove);
      setNeedsGas(!!data.needsGas);
    } catch {
      // Network/transient errors → keep previous state, don't surface
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ready || !authenticated) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    fetchBalance();
    intervalRef.current = setInterval(fetchBalance, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [ready, authenticated, fetchBalance]);

  return {
    eoa,
    depositWallet,
    balances,
    needsMove,
    needsGas,
    loading,
    refetch: fetchBalance,
  };
}
